<template>
    <div>
        <l-control position="topright">
            <div class="ispu-spatial-plan-control" @mousedown.stop @dblclick.stop @wheel.stop @click.stop>
                <button
                    v-if="!panelOpen"
                    class="ispu-toggle-btn"
                    @click="panelOpen = true"
                    title="Prostorni plan"
                >
                    <i class="fa fa-map"></i> Prostorni plan
                </button>
                <div v-show="panelOpen" class="ispu-panel">
                    <div class="ispu-panel-header">
                        <strong>Prostorni plan</strong>
                        <button class="ispu-panel-close" @click="panelOpen = false" title="Zatvori">
                            <i class="fa fa-times"></i>
                        </button>
                    </div>
                    <div class="ispu-plan-selector">
                        <select v-model="selectedPlanId" @change="onPlanSelected">
                            <option :value="null">-- Odaberite prostorni plan --</option>
                            <option v-for="plan in plans" :key="plan.id" :value="plan.id">
                                {{ plan.label }}
                            </option>
                        </select>
                        <div v-if="loadingPlans" class="text-muted m-t-5" style="font-size:11px;">
                            <i class="fa fa-spinner fa-spin"></i> Učitavanje planova...
                        </div>
                    </div>
                    <div v-if="loadingCatalog" class="text-muted" style="padding:6px 8px;font-size:11px;">
                        <i class="fa fa-spinner fa-spin"></i> Učitavanje slojeva...
                    </div>
                    <div v-if="catalog && catalog.list" class="ispu-layer-tree">
                        <div v-for="group in catalog.list" :key="group.id" class="layer-group">
                            <div
                                class="layer-group-header cursor-pointer"
                                @click="toggleGroup(group.id)"
                            >
                                <i :class="isGroupExpanded(group.id) ? 'fa fa-caret-down' : 'fa fa-caret-right'" class="m-r-5"></i>
                                <strong>{{ getLabel(group) }}</strong>
                            </div>
                            <div v-if="isGroupExpanded(group.id)" class="layer-group-children">
                                <template v-for="child in group.list">
                                    <!-- Leaf layer -->
                                    <div v-if="child.isLeaf && child.extensionData" :key="child.id" class="layer-leaf">
                                        <label class="m-b-0">
                                            <input
                                                type="checkbox"
                                                :checked="isLayerActive(child)"
                                                @change="toggleLayer(child)"
                                            />
                                            {{ getLabel(child) }}
                                        </label>
                                        <i v-if="isLayerLoading(child)" class="fa fa-spinner fa-spin" style="font-size:10px; color:#999;"></i>
                                        <a
                                            v-if="child.info && child.info.hasLegende"
                                            href="#"
                                            @click.prevent="openLegend(child)"
                                            class="legend-link"
                                            title="Legenda"
                                        >
                                            <i :class="loadingLegendId === child.id ? 'fa fa-spinner fa-spin' : 'fa fa-info-circle'"></i>
                                        </a>
                                    </div>
                                    <!-- Nested group -->
                                    <div v-else-if="!child.isLeaf && child.list" :key="child.id + 'nested'" class="layer-subgroup">
                                        <div
                                            class="layer-group-header cursor-pointer"
                                            @click="toggleGroup(child.id)"
                                        >
                                            <i :class="isGroupExpanded(child.id) ? 'fa fa-caret-down' : 'fa fa-caret-right'" class="m-r-5"></i>
                                            {{ getLabel(child) }}
                                        </div>
                                        <div v-if="isGroupExpanded(child.id)" class="layer-group-children">
                                            <div v-for="leaf in child.list" :key="leaf.id">
                                                <div v-if="leaf.isLeaf && leaf.extensionData" class="layer-leaf">
                                                    <label class="m-b-0">
                                                        <input
                                                            type="checkbox"
                                                            :checked="isLayerActive(leaf)"
                                                            @change="toggleLayer(leaf)"
                                                        />
                                                        {{ getLabel(leaf) }}
                                                    </label>
                                                    <i v-if="isLayerLoading(leaf)" class="fa fa-spinner fa-spin" style="font-size:10px; color:#999;"></i>
                                                    <a
                                                        v-if="leaf.info && leaf.info.hasLegende"
                                                        href="#"
                                                        @click.prevent="openLegend(leaf)"
                                                        class="legend-link"
                                                        title="Legenda"
                                                    >
                                                        <i :class="loadingLegendId === leaf.id ? 'fa fa-spinner fa-spin' : 'fa fa-info-circle'"></i>
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </l-control>

        <!-- Legend panel — free-positioned, draggable & resizable -->
        <div
            v-if="legendPanel.visible"
            ref="legendPanel"
            class="legend-panel"
            :class="{ 'legend-panel--interacting': legendPanelInteracting }"
            :style="legendPanelStyle"
            @mousedown.stop
            @dblclick.stop
            @wheel.stop
        >
            <div class="legend-panel-header" @mousedown.prevent="startDragLegend">
                <span class="legend-panel-title" :title="legendPanel.title">{{ legendPanel.title }}</span>
                <button class="legend-panel-close" @click="closeLegendPanel" title="Zatvori">
                    <i class="fa fa-times"></i>
                </button>
            </div>
            <div class="legend-panel-body">
                <img
                    v-if="legendPanel.type === 'image'"
                    :src="legendPanel.src"
                    style="max-width:100%;height:auto;"
                />
                <iframe
                    v-else-if="legendPanel.type === 'pdf'"
                    :src="legendPanel.src"
                    class="legend-panel-iframe"
                ></iframe>
                <iframe
                    v-else-if="legendPanel.type === 'url'"
                    :src="legendPanel.src"
                    class="legend-panel-iframe"
                ></iframe>
            </div>
            <div class="resize-handle resize-n" @mousedown.prevent.stop="startResizeLegend($event, 'n')"></div>
            <div class="resize-handle resize-e" @mousedown.prevent.stop="startResizeLegend($event, 'e')"></div>
            <div class="resize-handle resize-s" @mousedown.prevent.stop="startResizeLegend($event, 's')"></div>
            <div class="resize-handle resize-w" @mousedown.prevent.stop="startResizeLegend($event, 'w')"></div>
            <div class="resize-handle resize-nw" @mousedown.prevent.stop="startResizeLegend($event, 'nw')"></div>
            <div class="resize-handle resize-ne" @mousedown.prevent.stop="startResizeLegend($event, 'ne')"></div>
            <div class="resize-handle resize-se" @mousedown.prevent.stop="startResizeLegend($event, 'se')"></div>
            <div class="resize-handle resize-sw" @mousedown.prevent.stop="startResizeLegend($event, 'sw')"></div>
        </div>
    </div>
</template>

<script>
import L from 'leaflet';
import { LControl } from 'vue2-leaflet';
import ApiService from '@/components/utils/api.service.js';
import { createExternalWMSLayer } from '@/components/utils/wms.js';

const ISPU_API_BASE = "/amas/real-estate/api/ispu";
const DEFAULT_LAYER_LABEL = "Prostori/površine za razvoj i uređenje";

export default {
    name: 'ISPUSpatialPlanControl',
    components: {
        LControl,
    },
    props: {
        lat: {
            type: Number,
            required: true,
        },
        lng: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            plans: [],
            selectedPlanId: null,
            catalog: null,
            activeLayers: {},
            loadingLayers: {},
            expandedGroups: {},
            loadingPlans: false,
            loadingCatalog: false,
            loadingLegendId: null,
            panelOpen: false,
            legendPanel: {
                visible: false,
                type: null,
                src: null,
                title: '',
            },
            legendPanelRect: null,
            legendPanelInteracting: false,
        };
    },
    computed: {
        selectedPlan() {
            return this.plans.find(p => p.id === this.selectedPlanId);
        },
        legendPanelStyle() {
            if (!this.legendPanelRect) return {};
            return {
                left: this.legendPanelRect.left + 'px',
                top: this.legendPanelRect.top + 'px',
                width: this.legendPanelRect.width + 'px',
                height: this.legendPanelRect.height + 'px',
                bottom: 'auto',
            };
        },
    },
    mounted() {
        this.fetchPlans();
        this.$nextTick(() => {
            try {
                const map = this._findMapObject();
                if (!map) return;
                const osmLayer = L.tileLayer(
                    "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
                    { maxZoom: 19 }
                );
                // Try to find control layers to add as named base layer
                let parent = this.$parent;
                while (parent) {
                    if (parent.$refs && parent.$refs.controlLayers && parent.$refs.controlLayers.mapObject) {
                        parent.$refs.controlLayers.mapObject.addBaseLayer(osmLayer, "OSM");
                        return;
                    }
                    parent = parent.$parent;
                }
            } catch (e) {
                console.error("ISPUSpatialPlanControl: Failed to add OSM base layer:", e);
            }
        });
    },
    beforeDestroy() {
        this.removeAllActiveLayers();
    },
    methods: {
        _findMapObject() {
            let parent = this.$parent;
            while (parent) {
                if (parent.mapObject) return parent.mapObject;
                // KSimpleLeafletMap exposes leafletMapObject
                if (parent.leafletMapObject) return parent.leafletMapObject;
                parent = parent.$parent;
            }
            return null;
        },
        async fetchPlans() {
            this.loadingPlans = true;
            try {
                const response = await ApiService.get(
                    `${ISPU_API_BASE}/spatial-plans`,
                    { params: { lat: this.lat, lng: this.lng } }
                );
                this.plans = response.data;
            } catch (e) {
                console.error("ISPUSpatialPlanControl: Failed to fetch ISPU plans:", e);
                this.plans = [];
            }
            this.loadingPlans = false;
        },
        async onPlanSelected() {
            this.removeAllActiveLayers();
            this.catalog = null;
            this.expandedGroups = {};

            if (!this.selectedPlanId) return;

            const plan = this.selectedPlan;
            if (!plan) return;

            this.loadingCatalog = true;
            try {
                const response = await ApiService.get(
                    `${ISPU_API_BASE}/spatial-plan-catalog`,
                    { params: { id: plan.id, hash: plan.hash } }
                );
                this.catalog = response.data;
                this.autoSelectDefaultLayer();
            } catch (e) {
                console.error("ISPUSpatialPlanControl: Failed to fetch ISPU catalog:", e);
                this.catalog = null;
            }
            this.loadingCatalog = false;
        },
        autoSelectDefaultLayer() {
            if (!this.catalog || !this.catalog.list) return;
            const leaf = this.findLeafByLabel(this.catalog.list, DEFAULT_LAYER_LABEL);
            if (leaf) {
                this.toggleLayer(leaf);
                this.expandParentsOf(leaf);
            }
        },
        findLeafByLabel(nodes, targetLabel) {
            for (const node of nodes) {
                if (node.isLeaf && node.extensionData) {
                    const label = this.getLabel(node);
                    if (label === targetLabel) return node;
                }
                if (node.list) {
                    const found = this.findLeafByLabel(node.list, targetLabel);
                    if (found) return found;
                }
            }
            return null;
        },
        expandParentsOf(targetLeaf) {
            const expand = (nodes) => {
                for (const node of nodes) {
                    if (!node.isLeaf && node.list) {
                        if (this.treeContains(node.list, targetLeaf)) {
                            this.$set(this.expandedGroups, node.id, true);
                        }
                        expand(node.list);
                    }
                }
            };
            if (this.catalog && this.catalog.list) {
                expand(this.catalog.list);
            }
        },
        treeContains(nodes, target) {
            for (const node of nodes) {
                if (node.id === target.id) return true;
                if (node.list && this.treeContains(node.list, target)) return true;
            }
            return false;
        },
        getLabel(node) {
            if (node.label && typeof node.label === "object") {
                return node.label.hr || node.label.en || "";
            }
            return node.label || "";
        },
        toggleGroup(groupId) {
            this.$set(this.expandedGroups, groupId, !this.expandedGroups[groupId]);
        },
        isGroupExpanded(groupId) {
            return !!this.expandedGroups[groupId];
        },
        isLayerActive(leaf) {
            return !!this.activeLayers[leaf.id];
        },
        isLayerLoading(leaf) {
            return !!this.loadingLayers[leaf.id];
        },
        toggleLayer(leaf) {
            const map = this._findMapObject();
            if (!map) return;

            if (this.activeLayers[leaf.id]) {
                map.removeLayer(this.activeLayers[leaf.id].wmsLayer);
                this.$delete(this.activeLayers, leaf.id);
                this.$delete(this.loadingLayers, leaf.id);
            } else {
                const ext = leaf.extensionData;
                var wmsLayer;

                if (ext.isExternal) {
                    wmsLayer = createExternalWMSLayer(ext);
                } else {
                    var idMatch = ext.url.match(/id=(\d+)/);
                    var wmsId = idMatch ? idMatch[1] : "";
                    var wmsUrl = "https://ispu.mgipu.hr/api/v1/gis/prostorni-plan-wms?id=" + wmsId + "&layerHash=dummy-layer-hash&serviceId=" + encodeURIComponent(ext.serviceId);

                    var wmsOptions = {
                        layers: ext.wmsParams.layers,
                        format: ext.wmsParams.format || "image/png",
                        styles: "",
                        transparent: true,
                        maxZoom: 19,
                        zIndex: ext.zIndex || 250,
                    };
                    if (ext.wmsParams.version) {
                        wmsOptions.version = ext.wmsParams.version;
                    }
                    if (ext.opacity != null) {
                        wmsOptions.opacity = ext.opacity;
                    }
                    wmsLayer = L.tileLayer.wms(wmsUrl, wmsOptions);
                }

                this.$set(this.loadingLayers, leaf.id, true);
                var self = this;
                wmsLayer.on('loading', function () {
                    self.$set(self.loadingLayers, leaf.id, true);
                });
                wmsLayer.on('load', function () {
                    self.$set(self.loadingLayers, leaf.id, false);
                });

                wmsLayer.addTo(map);
                this.$set(this.activeLayers, leaf.id, { node: leaf, wmsLayer });
            }
        },
        removeAllActiveLayers() {
            const map = this._findMapObject();
            Object.values(this.activeLayers).forEach(({ wmsLayer }) => {
                if (map && map.hasLayer(wmsLayer)) {
                    map.removeLayer(wmsLayer);
                }
            });
            this.activeLayers = {};
            this.loadingLayers = {};
        },
        async openLegend(leaf) {
            if (this.loadingLegendId) return;
            this.loadingLegendId = leaf.id;

            const planLabel = this.selectedPlan ? this.selectedPlan.label : "";
            const layerLabel = this.getLabel(leaf);
            const title = `${planLabel} - ${layerLabel}`;

            try {
                const response = await ApiService.get(
                    `${ISPU_API_BASE}/spatial-plan-legend`,
                    { params: { id: leaf.id, type: "legende" } }
                );
                const data = response.data;
                if (!data || data.length === 0) return;

                const item = data[0];
                if (item.format === "image" && item.source) {
                    this.legendPanel = {
                        visible: true,
                        type: 'image',
                        src: `data:image/jpeg;base64,${item.source}`,
                        title,
                    };
                } else if (item.format === "pdf" && item.source) {
                    const blob = this.base64ToBlob(item.source, "application/pdf");
                    this.legendPanel = {
                        visible: true,
                        type: 'pdf',
                        src: URL.createObjectURL(blob),
                        title,
                    };
                } else if (item.url) {
                    this.legendPanel = {
                        visible: true,
                        type: 'url',
                        src: item.url,
                        title,
                    };
                }
            } catch (e) {
                console.error("ISPUSpatialPlanControl: Failed to fetch legend:", e);
            } finally {
                this.loadingLegendId = null;
            }
        },
        closeLegendPanel() {
            if (this.legendPanel.type === 'pdf' && this.legendPanel.src) {
                URL.revokeObjectURL(this.legendPanel.src);
            }
            this.legendPanel = { visible: false, type: null, src: null, title: '' };
            this.legendPanelRect = null;
        },
        initLegendPanelRect() {
            if (this.legendPanelRect) return;
            const el = this.$refs.legendPanel;
            if (!el) return;
            const parent = el.offsetParent || el.parentElement;
            const pRect = parent.getBoundingClientRect();
            const eRect = el.getBoundingClientRect();
            this.legendPanelRect = {
                left: eRect.left - pRect.left,
                top: eRect.top - pRect.top,
                width: eRect.width,
                height: eRect.height,
            };
        },
        disableMapDrag() {
            const map = this._findMapObject();
            if (map && map.dragging) map.dragging.disable();
        },
        enableMapDrag() {
            const map = this._findMapObject();
            if (map && map.dragging) map.dragging.enable();
        },
        startDragLegend(e) {
            this.initLegendPanelRect();
            if (!this.legendPanelRect) return;
            this.legendPanelInteracting = true;
            this.disableMapDrag();
            const startX = e.clientX;
            const startY = e.clientY;
            const startLeft = this.legendPanelRect.left;
            const startTop = this.legendPanelRect.top;

            const onMove = (ev) => {
                this.legendPanelRect = Object.assign({}, this.legendPanelRect, {
                    left: startLeft + (ev.clientX - startX),
                    top: startTop + (ev.clientY - startY),
                });
            };
            const onUp = () => {
                this.legendPanelInteracting = false;
                this.enableMapDrag();
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        },
        startResizeLegend(e, dir) {
            this.initLegendPanelRect();
            if (!this.legendPanelRect) return;
            this.legendPanelInteracting = true;
            this.disableMapDrag();
            const startX = e.clientX;
            const startY = e.clientY;
            const r = Object.assign({}, this.legendPanelRect);
            const MIN_W = 200;
            const MIN_H = 150;

            const onMove = (ev) => {
                const dx = ev.clientX - startX;
                const dy = ev.clientY - startY;
                const next = Object.assign({}, r);

                if (dir.includes('e')) next.width = Math.max(MIN_W, r.width + dx);
                if (dir.includes('s')) next.height = Math.max(MIN_H, r.height + dy);
                if (dir.includes('w')) {
                    const w = Math.max(MIN_W, r.width - dx);
                    next.left = r.left + r.width - w;
                    next.width = w;
                }
                if (dir.includes('n')) {
                    const h = Math.max(MIN_H, r.height - dy);
                    next.top = r.top + r.height - h;
                    next.height = h;
                }
                this.legendPanelRect = next;
            };
            const onUp = () => {
                this.legendPanelInteracting = false;
                this.enableMapDrag();
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        },
        base64ToBlob(base64, contentType) {
            const byteChars = atob(base64);
            const byteArrays = [];
            for (let offset = 0; offset < byteChars.length; offset += 512) {
                const slice = byteChars.slice(offset, offset + 512);
                const byteNumbers = new Array(slice.length);
                for (let i = 0; i < slice.length; i++) {
                    byteNumbers[i] = slice.charCodeAt(i);
                }
                byteArrays.push(new Uint8Array(byteNumbers));
            }
            return new Blob(byteArrays, { type: contentType });
        },
    },
};
</script>

<style scoped>
.ispu-spatial-plan-control {
    background: white;
    border-radius: 4px;
    box-shadow: 0 1px 5px rgba(0, 0, 0, 0.3);
    max-width: 320px;
}
.ispu-toggle-btn {
    background: white;
    border: none;
    padding: 6px 10px;
    cursor: pointer;
    font-size: 12px;
    width: 100%;
    text-align: left;
    border-radius: 4px;
}
.ispu-toggle-btn:hover {
    background: #f4f4f4;
}
.ispu-panel {
    font-size: 11px;
    min-width: 250px;
}
.ispu-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 10px;
    border-bottom: 1px solid #eee;
    font-size: 12px;
}
.ispu-panel-close {
    background: none;
    border: none;
    cursor: pointer;
    color: #999;
    font-size: 14px;
    padding: 0 2px;
    flex-shrink: 0;
    line-height: 1;
}
.ispu-panel-close:hover {
    color: #333;
}
.ispu-plan-selector {
    padding: 6px 8px;
    border-bottom: 1px solid #eee;
}
.ispu-plan-selector select {
    width: 100%;
    font-size: 11px;
}
.ispu-layer-tree {
    max-height: 350px;
    overflow-y: auto;
    padding: 6px 8px;
}
.layer-group {
    margin-bottom: 2px;
}
.layer-group-header {
    padding: 3px 2px;
    font-size: 11px;
    user-select: none;
    text-align: left;
}
.layer-group-header:hover {
    background: #f5f5f5;
}
.layer-group-children {
    padding-left: 14px;
}
.layer-leaf {
    padding: 2px 0;
    display: flex;
    align-items: center;
    gap: 4px;
}
.layer-leaf label {
    font-weight: normal;
    cursor: pointer;
    margin-bottom: 0;
    font-size: 11px;
    text-align: left;
}
.layer-leaf input[type="checkbox"] {
    margin-right: 4px;
}
.legend-link {
    color: #5bc0de;
    font-size: 11px;
    flex-shrink: 0;
}
.legend-link:hover {
    color: #31b0d5;
}
.layer-subgroup {
    margin-top: 2px;
}

/* Legend panel (free-positioned, draggable & resizable) */
.legend-panel {
    position: absolute;
    z-index: 1000;
    bottom: 10px;
    left: 10px;
    width: 340px;
    height: 400px;
    background: white;
    border-radius: 4px;
    box-shadow: 0 1px 5px rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
}
.legend-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 10px;
    border-bottom: 1px solid #eee;
    gap: 8px;
    cursor: move;
    flex-shrink: 0;
}
.legend-panel-title {
    font-size: 11px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
}
.legend-panel-close {
    background: none;
    border: none;
    cursor: pointer;
    color: #999;
    font-size: 14px;
    padding: 0 2px;
    flex-shrink: 0;
    line-height: 1;
}
.legend-panel-close:hover {
    color: #333;
}
.legend-panel-body {
    overflow: auto;
    padding: 10px;
    text-align: left;
    flex: 1;
    min-height: 0;
}
/* Block iframe from stealing pointer during drag/resize */
.legend-panel--interacting .legend-panel-body {
    pointer-events: none;
}
.legend-panel-iframe {
    width: 100%;
    height: 100%;
    border: none;
}

/* Resize handles */
.resize-handle {
    position: absolute;
}
.resize-n  { top: -3px;    left: 6px;   right: 6px;  height: 6px; cursor: n-resize; }
.resize-s  { bottom: -3px; left: 6px;   right: 6px;  height: 6px; cursor: s-resize; }
.resize-e  { right: -3px;  top: 6px;    bottom: 6px; width: 6px;  cursor: e-resize; }
.resize-w  { left: -3px;   top: 6px;    bottom: 6px; width: 6px;  cursor: w-resize; }
.resize-nw { top: -3px;    left: -3px;  width: 8px;  height: 8px; cursor: nw-resize; }
.resize-ne { top: -3px;    right: -3px; width: 8px;  height: 8px; cursor: ne-resize; }
.resize-se { bottom: -3px; right: -3px; width: 8px;  height: 8px; cursor: se-resize; }
.resize-sw { bottom: -3px; left: -3px;  width: 8px;  height: 8px; cursor: sw-resize; }
</style>
